import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAssets, fetchTotalBalances, fetchSpotBalances, fetchFuturesBalances, fetchAIBalances, fetchWallet, initializeWallet, updateCoinPrice } from '../../store/Slices/assetSlice';
import { Container, Typography, Box, Button, useMediaQuery, Fade, Skeleton, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { initializePriceWebSocket } from '../../services/binanceWebSocket';
import OverviewSection from './OverviewSection';
import SpotSection from './SpotSection';
import FuturesSection from './FuturesSection';
import AISection from './AISection';
import TopNav from '../../Components/NavComponents/TopNav';
import BottomNav from '../../Components/NavComponents/BottomNav';

// Utility to manage localStorage with versioning
const CACHE_KEY = 'assets_cache_v1';
const CACHE_DURATION = 5 * 60 * 1000;

const getCachedData = () => {
  const cached = localStorage.getItem(CACHE_KEY);
  if (!cached) return null;
  const { timestamp, data } = JSON.parse(cached);
  const now = Date.now();
  if (now - timestamp > CACHE_DURATION) {
    localStorage.removeItem(CACHE_KEY);
    return null;
  }
  return data;
};

const setCachedData = (data) => {
  localStorage.setItem(CACHE_KEY, JSON.stringify({ timestamp: Date.now(), data }));
};

// Fallback REST API call to fetch prices
const fetchPriceFromBinanceRest = async (symbol) => {
  try {
    const response = await fetch(`https://api.binance.com/api/v3/ticker/price?symbol=${symbol.toUpperCase()}USDT`);
    const data = await response.json();
    const price = parseFloat(data.price);
    console.log(`Fetched price for ${symbol} via REST API: ${price}`);
    return price;
  } catch (err) {
    console.error(`Error fetching price for ${symbol} via REST API:`, err);
    return 0;
  }
};

const AssetsPage = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { assets, spotBalances, totalBalances, futuresBalances, aiBalances, walletInitialized, loading, error, coinPrices } = useSelector((state) => state.assets);
  const [tabValue, setTabValue] = useState(0);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const wsRef = useRef(null);
  const prevSymbolsRef = useRef([]);

  // Load cached data on mount as a fallback
  useEffect(() => {
    const cachedData = getCachedData();
    if (cachedData) {
      dispatch({ type: 'assets/setCachedData', payload: cachedData });
      setIsDataLoaded(true);
    }
  }, [dispatch]);

  // Main data fetching logic
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setIsRefreshing(true);

        // Force fetch all data every time
        const assetsPromise = dispatch(fetchAssets({ forceRefresh: true })).unwrap();
        const walletPromise = dispatch(fetchWallet({ forceRefresh: true })).unwrap();

        await Promise.all([assetsPromise, walletPromise]);

        if (walletInitialized) {
          const balancePromises = [
            dispatch(fetchSpotBalances()).unwrap(),
            dispatch(fetchFuturesBalances()).unwrap(),
            dispatch(fetchAIBalances()).unwrap(),
            dispatch(fetchTotalBalances()).unwrap(),
          ];
          await Promise.all(balancePromises);

          console.log('spotBalances after fetching:', spotBalances);

          const cacheData = {
            assets,
            totalBalances,
            spotBalances,
            futuresBalances,
            aiBalances,
            walletInitialized,
          };
          setCachedData(cacheData);

          // Extract symbols for all assets
          const symbols = spotBalances
            .map(balance => balance.asset_id?.symbol?.toLowerCase())
            .filter(symbol => symbol && symbol !== 'usdt');

          console.log('Extracted symbols for WebSocket:', symbols);

          if (symbols.length > 0) {
            if (wsRef.current) {
              console.log('Closing existing WebSocket');
              wsRef.current.close();
            }
            console.log('Initializing WebSocket with symbols:', symbols);
            wsRef.current = initializePriceWebSocket(symbols, ({ symbol, price }) => {
              console.log(`Received price update: ${symbol} = ${price}`);
              dispatch(updateCoinPrice({ symbol, price }));
            });
            prevSymbolsRef.current = symbols;

            // Fallback: After a timeout, fetch missing prices via REST API
            setTimeout(async () => {
              for (const symbol of symbols) {
                if (!coinPrices[symbol] || coinPrices[symbol] === null) {
                  const price = await fetchPriceFromBinanceRest(symbol);
                  if (price) {
                    dispatch(updateCoinPrice({ symbol, price }));
                  }
                }
              }
            }, 5000);
          } else {
            console.log('No valid symbols to fetch prices for, skipping WebSocket initialization');
          }
        }

        setIsDataLoaded(true);
      } catch (err) {
        console.error('Error fetching data:', err);
        setIsDataLoaded(true);
      } finally {
        setIsRefreshing(false);
      }
    };

    fetchAllData();

    return () => {
      if (wsRef.current) {
        console.log('Cleaning up WebSocket');
        wsRef.current.close();
      }
    };
  }, [dispatch, walletInitialized]); // Removed hasFetchedData and other dependencies to force fetch on every mount

  // Log coinPrices to verify updates
  useEffect(() => {
    console.log('Current coinPrices:', coinPrices);
  }, [coinPrices]);

  const handleTabChange = (newValue) => {
    setTabValue(newValue);
  };

  const handleInitializeWallet = () => {
    dispatch(initializeWallet());
  };

  const handleNavigate = (section) => {
    setTabValue(section);
  };

  const handleRetry = () => {
    setIsDataLoaded(false);
    const fetchAllData = async () => {
      try {
        setIsRefreshing(true);

        // Force fetch all data every time
        const assetsPromise = dispatch(fetchAssets({ forceRefresh: true })).unwrap();
        const walletPromise = dispatch(fetchWallet({ forceRefresh: true })).unwrap();

        await Promise.all([assetsPromise, walletPromise]);

        if (walletInitialized) {
          const balancePromises = [
            dispatch(fetchSpotBalances()).unwrap(),
            dispatch(fetchFuturesBalances()).unwrap(),
            dispatch(fetchAIBalances()).unwrap(),
            dispatch(fetchTotalBalances()).unwrap(),
          ];
          await Promise.all(balancePromises);

          console.log('spotBalances after fetching:', spotBalances);

          const cacheData = {
            assets,
            totalBalances,
            spotBalances,
            futuresBalances,
            aiBalances,
            walletInitialized,
          };
          setCachedData(cacheData);

          // Extract symbols for all assets
          const symbols = spotBalances
            .map(balance => balance.asset_id?.symbol?.toLowerCase())
            .filter(symbol => symbol && symbol !== 'usdt');

          console.log('Extracted symbols for WebSocket:', symbols);

          if (symbols.length > 0) {
            if (wsRef.current) {
              console.log('Closing existing WebSocket');
              wsRef.current.close();
            }
            console.log('Initializing WebSocket with symbols:', symbols);
            wsRef.current = initializePriceWebSocket(symbols, ({ symbol, price }) => {
              console.log(`Received price update: ${symbol} = ${price}`);
              dispatch(updateCoinPrice({ symbol, price }));
            });
            prevSymbolsRef.current = symbols;

            // Fallback: After a timeout, fetch missing prices via REST API
            setTimeout(async () => {
              for (const symbol of symbols) {
                if (!coinPrices[symbol] || coinPrices[symbol] === null) {
                  const price = await fetchPriceFromBinanceRest(symbol);
                  if (price) {
                    dispatch(updateCoinPrice({ symbol, price }));
                  }
                }
              }
            }, 5000);
          } else {
            console.log('No valid symbols to fetch prices for, skipping WebSocket initialization');
          }
        }

        setIsDataLoaded(true);
      } catch (err) {
        console.error('Error fetching data:', err);
        setIsDataLoaded(true);
      } finally {
        setIsRefreshing(false);
      }
    };

    fetchAllData();
  };

  if (!isDataLoaded || (loading && spotBalances.length === 0)) {
    return (
      <Box sx={{ p: 3, mt: isMobile ? 0 : 8 }}>
        <Skeleton variant="rectangular" height={40} sx={{ mb: 2, borderRadius: 2 }} />
        <Skeleton variant="rectangular" height={200} sx={{ mb: 2, borderRadius: 2 }} />
        <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, mt: isMobile ? 0 : 8, textAlign: 'center' }}>
        <Typography sx={{ color: 'red', mb: 2 }}>Error: {error}</Typography>
        <Button
          variant="contained"
          sx={{
            bgcolor: theme.palette.brand.main,
            color: theme.palette.text.primary,
            borderRadius: 2,
            transition: 'all 0.3s',
            '&:hover': {
              bgcolor: theme.palette.brand.main,
              transform: 'translateY(-2px)',
              boxShadow: theme.shadows[4],
            },
          }}
          onClick={handleRetry}
        >
          Retry
        </Button>
      </Box>
    );
  }

  const tabs = ['Overview', 'Spot', 'Futures', 'AI'];

  return (
    <Box sx={{ bgcolor: theme.palette.background.default, minHeight: '100vh', position: 'relative' }}>
      {!isMobile && <TopNav />}
      <Box
        sx={{
          display: 'flex',
          overflowX: 'auto',
          whiteSpace: 'nowrap',
          bgcolor: theme.palette.background.paper,
          position: isMobile ? 'fixed' : 'relative',
          top: isMobile ? 0 : 'auto',
          mt: isMobile ? 0 : 8,
          width: '100%',
          zIndex: 1000,
          borderBottom: `1px solid ${theme.palette.text.secondary}20`,
          py: 1,
          px: 2,
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
        }}
      >
        {tabs.map((tab, index) => (
          <Box
            key={index}
            onClick={() => handleTabChange(index)}
            sx={{
              display: 'inline-block',
              px: 3,
              py: 1,
              mx: 1,
              borderRadius: 2,
              cursor: 'pointer',
              bgcolor: tabValue === index ? `${theme.palette.brand.main}20` : 'transparent',
              color: tabValue === index ? theme.palette.brand.main : theme.palette.text.secondary,
              fontWeight: tabValue === index ? 'bold' : 'medium',
              transition: 'all 0.3s',
              '&:hover': {
                bgcolor: `${theme.palette.brand.main}10`,
                color: theme.palette.brand.main,
                transform: 'translateY(-2px)',
              },
            }}
          >
            <Typography variant="body1">{tab}</Typography>
          </Box>
        ))}
      </Box>
      <Container
        sx={{
          py: isMobile ? 8 : 2,
          minHeight: '100vh',
        }}
      >
        {isRefreshing && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
            <CircularProgress size={20} sx={{ mr: 1 }} />
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              Refreshing...
            </Typography>
          </Box>
        )}
        {!walletInitialized ? (
          <Box textAlign="center" my={4}>
            <Typography variant="h6" gutterBottom sx={{ color: theme.palette.text.primary }}>
              Are you ready to trade?
            </Typography>
            <Button
              variant="contained"
              sx={{
                bgcolor: theme.palette.brand.main,
                color: theme.palette.text.primary,
                borderRadius: 2,
                transition: 'all 0.3s',
                '&:hover': {
                  bgcolor: theme.palette.brand.main,
                  transform: 'translateY(-2px)',
                  boxShadow: theme.shadows[4],
                },
              }}
              onClick={handleInitializeWallet}
            >
              Yes, Initialize My Wallets
            </Button>
          </Box>
        ) : (
          <Fade in={true} timeout={1000} key={tabValue}>
            <Box>
              {tabValue === 0 && <OverviewSection handleNavigate={handleNavigate} />}
              {tabValue === 1 && <SpotSection />}
              {tabValue === 2 && <FuturesSection />}
              {tabValue === 3 && <AISection />}
            </Box>
          </Fade>
        )}
      </Container>
      {isMobile && <BottomNav navValue={3} />}
    </Box>
  );
};

export default AssetsPage;